export type CardRect = {
  id: string;
  col: number;
  row: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerY: number;
};

type ConnectorLink = {
  id: string;
  fromStepId: string;
  toStepId: string;
};

type ConnectorPath = {
  id: string;
  d: string;
  fromStepId: string;
  toStepId: string;
};

type Point = [number, number];

export const cornerRadius = 12;
export const loopbackMargin = 16;
const connectorTrackOffset = 8;

type ColumnBounds = {
  left: number;
  right: number;
};

/** Build an SVG path from orthogonal waypoints with rounded corners. */
function roundedPath(pts: Point[], r: number): string {
  if (pts.length < 2) return "";
  if (pts.length === 2) {
    return `M ${pts[0]![0]} ${pts[0]![1]} L ${pts[1]![0]} ${pts[1]![1]}`;
  }

  let d = `M ${pts[0]![0]} ${pts[0]![1]}`;

  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i - 1]!;
    const [cx, cy] = pts[i]!;
    const [nx, ny] = pts[i + 1]!;

    const len1 = Math.abs(cx - px) + Math.abs(cy - py);
    const len2 = Math.abs(nx - cx) + Math.abs(ny - cy);
    const actualR = Math.min(r, len1 / 2, len2 / 2);

    if (actualR <= 0) {
      d += ` L ${cx} ${cy}`;
      continue;
    }

    const ux1 = cx === px ? 0 : cx > px ? 1 : -1;
    const uy1 = cy === py ? 0 : cy > py ? 1 : -1;
    const ux2 = nx === cx ? 0 : nx > cx ? 1 : -1;
    const uy2 = ny === cy ? 0 : ny > cy ? 1 : -1;

    d += ` L ${cx - ux1 * actualR} ${cy - uy1 * actualR}`;
    d += ` Q ${cx} ${cy} ${cx + ux2 * actualR} ${cy + uy2 * actualR}`;
  }

  const last = pts[pts.length - 1]!;
  d += ` L ${last[0]} ${last[1]}`;
  return d;
}

function getColumnBounds(cards: Map<string, CardRect>) {
  const bounds = new Map<number, ColumnBounds>();

  for (const card of cards.values()) {
    const existing = bounds.get(card.col);
    if (existing) {
      existing.left = Math.min(existing.left, card.left);
      existing.right = Math.max(existing.right, card.right);
      continue;
    }
    bounds.set(card.col, { left: card.left, right: card.right });
  }

  return bounds;
}

function getGapTrackX(
  columnBounds: Map<number, ColumnBounds>,
  leftCol: number,
  rightCol: number
) {
  const left = columnBounds.get(leftCol);
  const right = columnBounds.get(rightCol);
  if (!left || !right) return null;
  return left.right + (right.left - left.right) / 2;
}

/**
 * Routes orthogonal connectors between workflow cards.
 *
 * All paths use only horizontal and vertical segments with rounded
 * corners. Forward links route through a shared target-side spine so
 * sibling verticals line up; loopback links route via a channel above
 * or below the grid.
 */
export function routeConnectors(
  cards: Map<string, CardRect>,
  links: ConnectorLink[]
): ConnectorPath[] {
  if (links.length === 0 || cards.size === 0) return [];

  const paths: ConnectorPath[] = [];

  // Group links by source step
  const linksBySource = new Map<string, ConnectorLink[]>();
  for (const link of links) {
    const group = linksBySource.get(link.fromStepId);
    if (group) {
      group.push(link);
    } else {
      linksBySource.set(link.fromStepId, [link]);
    }
  }

  // Compute grid bounds for loopback channel positioning
  let gridTop = Infinity;
  let gridBottom = -Infinity;
  for (const card of cards.values()) {
    if (card.top < gridTop) gridTop = card.top;
    if (card.bottom > gridBottom) gridBottom = card.bottom;
  }
  const columnBounds = getColumnBounds(cards);

  for (const [, outgoing] of linksBySource) {
    const source = cards.get(outgoing[0]!.fromStepId);
    if (!source) continue;

    const resolved = outgoing
      .map((link) => ({ link, card: cards.get(link.toStepId) }))
      .filter(
        (t): t is { link: ConnectorLink; card: CardRect } => t.card != null
      );

    const forward = resolved.filter((t) => t.card.col > source.col);
    const backward = resolved.filter((t) => t.card.col <= source.col);

    // --- Forward routing ---
    if (forward.length > 0) {
      const nearestCol = Math.min(...forward.map((t) => t.card.col));
      const nearestLeft = Math.min(...forward.map((t) => t.card.left));
      // Place the spine in the gap immediately to the right of the source
      // column so the vertical segment never passes through intermediate cards.
      const spineGapRight = columnBounds.has(source.col + 1)
        ? source.col + 1
        : nearestCol;
      const spineX =
        getGapTrackX(columnBounds, source.col, spineGapRight) ??
        source.right + (nearestLeft - source.right) / 2;

      for (const { link, card: target } of forward) {
        if (Math.abs(source.centerY - target.centerY) < 1) {
          // Same row: straight horizontal
          paths.push({
            id: link.id,
            fromStepId: link.fromStepId,
            toStepId: link.toStepId,
            d: roundedPath(
              [
                [source.right, source.centerY],
                [target.left, target.centerY],
              ],
              cornerRadius
            ),
          });
        } else {
          // Z-route through spine
          paths.push({
            id: link.id,
            fromStepId: link.fromStepId,
            toStepId: link.toStepId,
            d: roundedPath(
              [
                [source.right, source.centerY],
                [spineX, source.centerY],
                [spineX, target.centerY],
                [target.left, target.centerY],
              ],
              cornerRadius
            ),
          });
        }
      }
    }

    // --- Backward / loopback routing ---
    for (const { link, card: target } of backward) {
      const routeAbove = source.row <= target.row;
      const channelY = routeAbove
        ? gridTop - loopbackMargin
        : gridBottom + loopbackMargin;

      const exitX =
        getGapTrackX(columnBounds, source.col, source.col + 1) ??
        source.right + connectorTrackOffset;
      const entryX =
        getGapTrackX(columnBounds, target.col - 1, target.col) ??
        target.left - connectorTrackOffset;

      paths.push({
        id: link.id,
        fromStepId: link.fromStepId,
        toStepId: link.toStepId,
        d: roundedPath(
          [
            [source.right, source.centerY],
            [exitX, source.centerY],
            [exitX, channelY],
            [entryX, channelY],
            [entryX, target.centerY],
            [target.left, target.centerY],
          ],
          cornerRadius
        ),
      });
    }
  }

  return paths;
}
